import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Edit2, Trash2, Mail, Phone, Award, Eye, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/utils/api";

const Trainers = () => {
  const navigate = useNavigate();
  const { settings, formatCurrency } = useSettings();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    specialization: "",
    payment_type: "per_class",
    payment_rate: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      const trainersData = response.data;
      
      // Fetch stats for each trainer
      const trainersWithStats = await Promise.all(
        trainersData.map(async (trainer) => {
          try {
            const statsRes = await api.get(`/trainers/${trainer.id}/stats`);
            return { ...trainer, stats: statsRes.data };
          } catch (error) {
            return { ...trainer, stats: null };
          }
        })
      );
      
      setTrainers(trainersWithStats);
    } catch (error) {
      toast.error("Failed to load trainers");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }
    
    if (!formData.phone || !/^[\+]?[0-9]{10,15}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      errors.phone = "Phone must be 10-15 digits";
    }
    
    if (!formData.specialization || formData.specialization.trim().length < 3) {
      errors.specialization = "Specialization must be at least 3 characters";
    }
    
    if (!formData.payment_rate || parseFloat(formData.payment_rate) <= 0) {
      errors.payment_rate = "Payment rate must be greater than 0";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        payment_rate: parseFloat(formData.payment_rate)
      };
      
      if (editingTrainer) {
        await api.patch(`/trainers/${editingTrainer.id}`, payload);
        toast.success("Trainer updated successfully");
      } else {
        await api.post('/trainers', payload);
        toast.success("Trainer added successfully");
      }
      fetchTrainers();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save trainer");
    }
  };

  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    setFormData({
      name: trainer.name,
      email: trainer.email,
      phone: trainer.phone,
      specialization: trainer.specialization,
      payment_type: trainer.payment_type,
      payment_rate: trainer.payment_rate.toString(),
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this trainer?")) {
      try {
        await api.delete(`/trainers/${id}`);
        toast.success("Trainer deleted successfully");
        fetchTrainers();
      } catch (error) {
        toast.error("Failed to delete trainer");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      specialization: "",
      payment_type: "per_class",
      payment_rate: "",
    });
    setFormErrors({});
    setEditingTrainer(null);
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      per_class: "Per Class",
      daily: "Daily",
      monthly: "Monthly"
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="trainers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Trainers
          </h1>
          <p className="text-zinc-500">Manage trainers and staff</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-trainer-btn"
              className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
            >
              <Plus size={20} className="mr-2" />
              Add Trainer
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="trainer-dialog-description" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTrainer ? "Edit Trainer" : "Add New Trainer"}</DialogTitle>
            </DialogHeader>
            <p id="trainer-dialog-description" className="sr-only">
              {editingTrainer ? "Edit trainer information" : "Add a new trainer to the gym"}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="trainer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={formErrors.name ? "border-red-500" : ""}
                  required
                />
                {formErrors.name && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="trainer-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={formErrors.email ? "border-red-500" : ""}
                  required
                />
                {formErrors.email && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="trainer-phone-input"
                  placeholder="+1234567890 or 1234567890"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={formErrors.phone ? "border-red-500" : ""}
                  required
                />
                {formErrors.phone && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>
                )}
              </div>
              <div>
                <Label htmlFor="specialization">Specialization *</Label>
                <Input
                  id="specialization"
                  data-testid="trainer-specialization-input"
                  placeholder="e.g., Strength Training, Yoga, CrossFit"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  className={formErrors.specialization ? "border-red-500" : ""}
                  required
                />
                {formErrors.specialization && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.specialization}</p>
                )}
              </div>
              <div>
                <Label htmlFor="payment_type">Payment Type *</Label>
                <Select
                  value={formData.payment_type}
                  onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
                >
                  <SelectTrigger data-testid="trainer-payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_class">Per Class</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="monthly">Monthly Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment_rate">Payment Rate ($) *</Label>
                <Input
                  id="payment_rate"
                  type="number"
                  step="0.01"
                  data-testid="trainer-payment-rate-input"
                  placeholder="e.g., 50 (per class), 200 (daily), 5000 (monthly)"
                  value={formData.payment_rate}
                  onChange={(e) => setFormData({ ...formData, payment_rate: e.target.value })}
                  className={formErrors.payment_rate ? "border-red-500" : ""}
                  required
                />
                {formErrors.payment_rate && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.payment_rate}</p>
                )}
                <p className="text-xs text-zinc-500 mt-1">
                  {formData.payment_type === 'per_class' && 'Amount paid per class conducted'}
                  {formData.payment_type === 'daily' && 'Amount paid per day worked'}
                  {formData.payment_type === 'monthly' && 'Fixed monthly salary'}
                </p>
              </div>
              <Button 
                type="submit" 
                data-testid="trainer-submit-btn"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              >
                {editingTrainer ? "Update" : "Add"} Trainer
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Trainers Table - Desktop */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full whitespace-nowrap">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">S.No</th>
              <th className="px-6 py-4 text-left">Trainer Info</th>
              <th className="px-6 py-4 text-left">Specialization</th>
              <th className="px-6 py-4 text-left">Payment Rate</th>
              <th className="px-6 py-4 text-left">Sessions</th>
              <th className="px-6 py-4 text-left">Total Paid</th>
              <th className="px-6 py-4 text-left">Pending</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {trainers.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-zinc-500">
                  No trainers found. Add your first trainer to get started.
                </td>
              </tr>
            ) : (
              trainers.map((trainer, index) => (
                <tr
                  key={trainer.id}
                  data-testid={`trainer-row-${trainer.id}`}
                  onClick={() => navigate(`/trainers/${trainer.id}`)}
                  className="hover:bg-zinc-100 transition-colors border-b border-zinc-100 last:border-0 cursor-pointer even:bg-zinc-50"
                >
                  <td className="px-6 py-4 font-medium text-zinc-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="font-bold text-zinc-900 mb-1">{trainer.name}</div>
                        <div className="flex flex-col gap-0.5 text-xs text-zinc-500">
                          <div className="flex items-center gap-1">
                            <Mail size={12} />
                            {trainer.email}
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone size={12} />
                            {trainer.phone}
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${
                          trainer.status === 'active' 
                            ? 'bg-lime-100 text-lime-800' 
                            : 'bg-zinc-100 text-zinc-800'
                        }`}>
                          {trainer.status}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-700">
                      <Award size={14} className="text-zinc-400" />
                      {trainer.specialization}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-zinc-900 font-bold">
                        <span className="text-lime-500">{settings.currency}</span>
                        <span>{trainer.payment_rate || 0}</span>
                      </div>
                      <span className="text-xs text-zinc-500">
                        {getPaymentTypeLabel(trainer.payment_type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {trainer.stats ? (
                      <div className="text-sm">
                        <div className="font-bold text-zinc-900">{trainer.stats.total_sessions}</div>
                        <div className="text-xs text-zinc-500">{trainer.stats.total_hours}h total</div>
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          {trainer.stats.current_month_sessions} this month
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {trainer.stats ? (
                      <div className="flex items-center gap-1 text-green-700 font-bold">
                        <span>{settings.currency}</span>
                        <span>{trainer.stats.total_earnings.toFixed(2)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {trainer.stats ? (
                      trainer.stats.pending_amount > 0 ? (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-1 text-orange-700 font-bold">
                            <span>{settings.currency}</span>
                            <span>{trainer.stats.pending_amount.toFixed(2)}</span>
                          </div>
                          <div className="text-xs text-orange-600 mt-0.5">Due</div>
                        </div>
                      ) : (
                        <div className="text-xs text-green-600 font-medium">✓ Paid up</div>
                      )
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`edit-trainer-${trainer.id}`}
                        onClick={(e) => { e.stopPropagation(); handleEdit(trainer); }}
                        className="p-2 hover:bg-zinc-100 rounded-md transition-colors"
                      >
                        <Edit2 size={16} className="text-zinc-600" />
                      </button>
                      <button
                        data-testid={`delete-trainer-${trainer.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(trainer.id); }}
                        className="p-2 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Trainers Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {trainers.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500">
            No trainers found. Add your first trainer to get started.
          </div>
        ) : (
          trainers.map((trainer) => (
            <div
              key={trainer.id}
              data-testid={`trainer-card-${trainer.id}`}
              onClick={() => navigate(`/trainers/${trainer.id}`)}
              className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm cursor-pointer"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-zinc-900 text-lg">{trainer.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                    <Mail size={12} />
                    {trainer.email}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                    <Phone size={12} />
                    {trainer.phone}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-600 mt-2">
                    <Award size={12} className="text-zinc-400" />
                    {trainer.specialization}
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  trainer.status === 'active' 
                    ? 'bg-lime-100 text-lime-800' 
                    : 'bg-zinc-100 text-zinc-800'
                }`}>
                  {trainer.status}
                </span>
              </div>

              {/* Payment Info */}
              <div className="bg-zinc-50 rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-zinc-500">Payment Rate:</span>
                  <div className="flex items-center gap-1 text-zinc-900 font-bold">
                    <span className="text-lime-500">{settings.currency}</span>
                    <span>{trainer.payment_rate || 0}</span>
                  </div>
                </div>
                <div className="text-xs text-zinc-500 text-center">
                  {getPaymentTypeLabel(trainer.payment_type)}
                </div>
              </div>

              {/* Stats */}
              {trainer.stats && (
                <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-zinc-100">
                  <div className="text-center">
                    <div className="text-lg font-bold text-zinc-900">{trainer.stats.total_sessions}</div>
                    <div className="text-xs text-zinc-500">Sessions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-700">{settings.currency}{trainer.stats.total_earnings.toFixed(2)}</div>
                    <div className="text-xs text-zinc-500">Earned</div>
                  </div>
                  <div className="text-center">
                    {trainer.stats.pending_amount > 0 ? (
                      <>
                        <div className="text-lg font-bold text-orange-700">{settings.currency}{trainer.stats.pending_amount.toFixed(2)}</div>
                        <div className="text-xs text-orange-600">Pending</div>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-bold text-green-600">✓</div>
                        <div className="text-xs text-green-600">Paid up</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleEdit(trainer); }}
                  className="flex-1"
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleDelete(trainer.id); }}
                  className="px-3 text-red-600 hover:text-red-700"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Trainers;